import type { IVoiceVideoEmitterInstance, IVoiceVideoEmitterSettings, StopReturnType } from "@whatslly/types/instant-messaging/voiceVideoEmitter";
import { DEFAULT_VOICE_VIDEO_EMITTER_SETTINGS } from "@whatslly/types/instant-messaging/voiceVideoEmitter";
import { pEventIterator } from "p-event";
import pLimit from "p-limit";
import type { ActiveCall, ContactModel, ModelType } from "@whatslly/types/instant-messaging/WhatsappTypes";
import get from "lodash/get";
import type { WhatsAppApi } from "@whatslly/instant-messaging/whatsapp/injectWhatsappApi";
import { injectWhatsappApi } from "@whatslly/instant-messaging/whatsapp/injectWhatsappApi";
import { getNamespace, injectWhatsappApiComet } from "@whatslly/instant-messaging/whatsapp/injectWhatsappApiComet";
import type { IMSelectorsUnion } from "@whatslly/types/integrations/im/IMSelectorsTypes";
import type { WhatsappSelectorsType } from "@whatslly/types/integrations/im/WhatsappGetSelectorsResponse";
import { getPrettyPhone } from "@whatslly/commons/utils";
import type { InstantMessagingService } from "@whatslly/types/instant-messaging/InstantMessagingService";
const WAP_LID_SUFFIX = "@lid";

const TARGET_NODE_NAME = "voip-shared-buffer-playback-processor";

const RECORDER_PROCESSOR_NAME = "tap-recorder";

const WORKLET_SRC = `
  class TapRecorder extends AudioWorkletProcessor {
    process(inputs) {
      const ch0 = inputs?.[0]?.[0];
      if (ch0) this.port.postMessage(ch0.slice(0));
      return true;
    }
  }
  registerProcessor('${RECORDER_PROCESSOR_NAME}', TapRecorder);
`;

interface RecorderState {
  filename: () => string;
  gain: number;

  includeMic: boolean;

  started: boolean;
  attached: boolean;

  targetNode: AudioWorkletNode | null;
  tapNode: AudioWorkletNode | null;
  frames: Float32Array[];

  micStream: MediaStream | null;
  micSourceByCtx: WeakMap<BaseAudioContext, MediaStreamAudioSourceNode>;
  micConnectedToTap: { ctx: BaseAudioContext; source: MediaStreamAudioSourceNode; tap: AudioWorkletNode } | null;
}

declare const window: Window & {
  importNamespace: (module: string) => any;
  importDefault: (module: string) => any;
  AudioWorkletNode: typeof AudioWorkletNode;
};

export class VoiceVideoScraper implements IVoiceVideoEmitterInstance {
  private static activeCall: ActiveCall | null = null;

  private static waRecInstalled = false;
  private static originalAudioWorkletNode: typeof AudioWorkletNode | null = null;

  private static installedCtx = new WeakSet<BaseAudioContext>();

  private static state: RecorderState = {
    filename: () => `audio-recording-${Date.now()}.wav`,
    gain: 1,

    includeMic: true,

    started: false,
    attached: false,
    targetNode: null,
    tapNode: null,
    frames: [],

    micStream: null,
    micSourceByCtx: new WeakMap(),
    micConnectedToTap: null,
  };

  private readonly settings: IVoiceVideoEmitterSettings;
  private readonly logger: {
    debug: (data: any, message: string) => void;
    warn: (data: any, message: string) => void;
    error: (data: any, message: string) => void;
  };
  private readonly mediaLimit = pLimit(10);
  private readonly selectors: WhatsappSelectorsType;
  private readonly whatsappAPI: WhatsAppApi;

  private readonly eventListeners: Record<
    "changeIsInConnectedCall",
    {
      on: string;
      off: string;
      bind: string;
      event: string;
    }[]
  >;

  public static selectors: WhatsappSelectorsType | undefined;
  public static whatsappAPI: WhatsAppApi | undefined;
  private static handlers = new Map<string, Map<(...args: any[]) => void, () => void>>();

  /** Set from constructor so static methods can log (e.g. attachIfPossible, installHook). */
  private static staticLogger: VoiceVideoScraper["logger"] | null = null;

  private static readonly noopLogger = {
    debug: (_data: any, _message: string) => {},
    warn: (_data: any, _message: string) => {},
    error: (_data: any, _message: string) => {},
  };

  constructor(
    settings: Partial<IVoiceVideoEmitterSettings>,
    logger: {
      debug: (_data: any, _message: string) => void;
      warn: (data: any, message: string) => void;
      error: (data: any, message: string) => void;
    },
    readonly instantMessagingService: InstantMessagingService
  ) {
    this.logger = logger ?? VoiceVideoScraper.noopLogger;
    VoiceVideoScraper.staticLogger = this.logger;
    if (!VoiceVideoScraper.selectors || !VoiceVideoScraper.whatsappAPI) {
      this.logger.error(
        { hasSelectors: Boolean(VoiceVideoScraper.selectors), hasWhatsappAPI: Boolean(VoiceVideoScraper.whatsappAPI) },
        "VoiceVideoScraper.inject must be called before usage"
      );
      throw new Error("VoiceVideoScraper.inject must be called before usage");
    }
    this.selectors = VoiceVideoScraper.selectors;
    this.whatsappAPI = VoiceVideoScraper.whatsappAPI;

    this.settings = {
      abortSignal: settings.abortSignal ?? DEFAULT_VOICE_VIDEO_EMITTER_SETTINGS.abortSignal,
    };
    this.settings.abortSignal.addEventListener("abort", () => this.mediaLimit.clearQueue(), { once: true });

    this.eventListeners = {
      changeIsInConnectedCall: [
        {
          on: this.selectors?.callCollection?.onFn ?? "CallCollection.on",
          off: this.selectors?.callCollection?.offFn ?? "CallCollection.off",
          bind: this.selectors?.callCollection?.onFnBind ?? "CallCollection",
          event: this.selectors?.callCollection?.changeIsInConnectedCall ?? "change:isInConnectedCall",
        },
      ],
    };
  }

  inject(selectors: IMSelectorsUnion): boolean {
    return VoiceVideoScraper.inject(selectors);
  }

  restoreOriginalWindow(): void {
    return VoiceVideoScraper.restoreOriginalWindow();
  }

  static inject(selectors: IMSelectorsUnion): boolean {
    if ("ng" in selectors) {
      VoiceVideoScraper.selectors ??= selectors;
      VoiceVideoScraper.whatsappAPI =
        (window as any).importNamespace === undefined ? injectWhatsappApi(selectors) : injectWhatsappApiComet(selectors);
    }
    const res = VoiceVideoScraper.installHook();
    return Boolean(res);
  }

  addEventListener(event: keyof VoiceVideoScraper["eventListeners"], callback: (data: { isConnected: boolean }) => void) {
    let handlers = VoiceVideoScraper.handlers.get(event);
    if (!handlers) {
      handlers = new Map();
      VoiceVideoScraper.handlers.set(event, handlers);
    }

    const listeners = this.eventListeners[event];
    const handler = (isConnected: boolean) => {
      callback({ isConnected });
    };

    const removeEventListener = () => {
      listeners.forEach(({ off, bind, event }) => {
        const obj = get(this.whatsappAPI, bind) ?? (this.whatsappAPI as any).CallCollection;
        get(this.whatsappAPI as any, off).call(obj, event, handler);
      });
    };

    listeners.forEach(({ on, bind, event }) => {
      const obj = get(this.whatsappAPI, bind) ?? (this.whatsappAPI as any).CallCollection;
      get(this.whatsappAPI as any, on).call(obj, event, handler);
    });

    handlers.set(callback, removeEventListener);
    return removeEventListener;
  }

  static removeEventListener(event: string, callback: any) {
    const handlers = VoiceVideoScraper.handlers.get(event);
    handlers?.get(callback)?.();
    handlers?.delete(callback);
  }

  removeEventListener(event: string, callback: any) {
    VoiceVideoScraper.removeEventListener(event, callback);
  }

  private get newActiveCallIterator() {
    const events = ["changeIsInConnectedCall"];
    return pEventIterator(this, events, { rejectionEvents: [], signal: this.settings.abortSignal });
  }

  private static encodeWav16(float32: Float32Array, sampleRate: number): ArrayBuffer {
    const bytesPerSample = 2;
    const buffer = new ArrayBuffer(44 + float32.length * bytesPerSample);
    const view = new DataView(buffer);
    const writeStr = (off: number, s: string) => {
      for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
    };

    writeStr(0, "RIFF");
    view.setUint32(4, 36 + float32.length * bytesPerSample, true);
    writeStr(8, "WAVE");
    writeStr(12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true); // mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * bytesPerSample, true);
    view.setUint16(32, bytesPerSample, true);
    view.setUint16(34, 16, true);
    writeStr(36, "data");
    view.setUint32(40, float32.length * bytesPerSample, true);

    let off = 44;
    for (let i = 0; i < float32.length; i++) {
      let s = float32[i];
      if (s > 1) s = 1;
      if (s < -1) s = -1;
      view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      off += 2;
    }
    return buffer;
  }

  private static flattenFrames(frames: Float32Array[]): Float32Array {
    let total = 0;
    for (const f of frames) total += f.length;
    const pcm = new Float32Array(total);
    let o = 0;
    for (const f of frames) {
      pcm.set(f, o);
      o += f.length;
    }
    return pcm;
  }

  private static async ensureWorklet(ctx: BaseAudioContext): Promise<void> {
    if (VoiceVideoScraper.installedCtx.has(ctx)) return;
    if (!ctx?.audioWorklet?.addModule) {
      VoiceVideoScraper.staticLogger?.error(
        { hasCtx: Boolean(ctx), hasAudioWorklet: Boolean(ctx?.audioWorklet), hasAddModule: Boolean(ctx?.audioWorklet?.addModule) },
        "VoiceVideoScraper: AudioWorklet not available on this AudioContext"
      );
      throw new Error("AudioWorklet not available on this AudioContext");
    }
    try {
      const url = URL.createObjectURL(new Blob([WORKLET_SRC], { type: "text/javascript" }));
      await ctx.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);
      VoiceVideoScraper.installedCtx.add(ctx);
    } catch (err) {
      VoiceVideoScraper.staticLogger?.error(
        { err: err instanceof Error ? err.message : String(err), sampleRate: ctx.sampleRate },
        "VoiceVideoScraper: ensureWorklet addModule failed"
      );
      throw err;
    }
  }

  private static async ensureMicStream(): Promise<MediaStream> {
    const { state } = VoiceVideoScraper;
    if (state.micStream) return state.micStream;
    try {
      state.micStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      return state.micStream;
    } catch (err) {
      VoiceVideoScraper.staticLogger?.warn(
        { err: err instanceof Error ? err.message : String(err), name: err instanceof Error ? err.name : undefined },
        "VoiceVideoScraper: getUserMedia for mic failed"
      );
      throw err;
    }
  }

  private static disconnectPreviousMic(): void {
    const { state } = VoiceVideoScraper;
    try {
      state.micConnectedToTap?.source?.disconnect();
    } catch {
      // non-fatal
    }
    state.micConnectedToTap = null;
  }

  /** Stop all mic stream tracks so the browser releases the microphone (indicator goes away). */
  private static stopMicStream(): void {
    const { state } = VoiceVideoScraper;
    if (!state.micStream) return;
    try {
      state.micStream.getTracks().forEach((track) => track.stop());
    } catch (err) {
      VoiceVideoScraper.staticLogger?.warn({ err: err instanceof Error ? err.message : String(err) }, "VoiceVideoScraper: stopMicStream failed");
    }
    state.micStream = null;
  }

  private static cleanupRecordingResources(): void {
    const { state } = VoiceVideoScraper;

    try {
      if (state.targetNode && state.tapNode) {
        state.targetNode.disconnect(state.tapNode);
      }
    } catch {
      // non-fatal
    }
    try {
      state.tapNode?.disconnect();
    } catch {
      // non-fatal
    }

    VoiceVideoScraper.disconnectPreviousMic();
    VoiceVideoScraper.stopMicStream();

    state.started = false;
    state.attached = false;
    state.tapNode = null;
    state.targetNode = null;
    state.frames = [];
  }

  private static async connectMicToTap(ctx: AudioContext, tap: AudioWorkletNode): Promise<void> {
    const { state } = VoiceVideoScraper;
    if (!state.includeMic) return;

    try {
      await VoiceVideoScraper.ensureMicStream();
    } catch (err) {
      VoiceVideoScraper.staticLogger?.warn(
        { err: err instanceof Error ? err.message : String(err) },
        "VoiceVideoScraper: connectMicToTap skipped (mic unavailable)"
      );
      return;
    }

    VoiceVideoScraper.disconnectPreviousMic();

    let micSource = state.micSourceByCtx.get(ctx);
    if (!micSource) {
      micSource = ctx.createMediaStreamSource(state.micStream!);
      state.micSourceByCtx.set(ctx, micSource);
    }

    micSource.connect(tap);

    state.micConnectedToTap = { ctx, source: micSource, tap };
  }

  private static async attachIfPossible(): Promise<void> {
    const { state } = VoiceVideoScraper;
    if (!state.started || !state.targetNode || state.attached) return;

    const node = state.targetNode;
    const ctx = node.context as AudioContext;

    try {
      await VoiceVideoScraper.ensureWorklet(ctx);
    } catch (err) {
      VoiceVideoScraper.staticLogger?.error(
        { err: err instanceof Error ? err.message : String(err), sampleRate: ctx.sampleRate },
        "VoiceVideoScraper: attachIfPossible ensureWorklet failed"
      );
      throw err;
    }

    try {
      const tap = new AudioWorkletNode(ctx, RECORDER_PROCESSOR_NAME);
      state.frames = [];
      tap.port.onmessage = (e: MessageEvent) => state.frames.push(new Float32Array((e as any).data));

      node.connect(tap);

      tap.connect(ctx.destination);

      state.tapNode = tap;
      state.attached = true;

      await VoiceVideoScraper.connectMicToTap(ctx, tap);
    } catch (err) {
      VoiceVideoScraper.staticLogger?.error(
        { err: err instanceof Error ? err.message : String(err) },
        "VoiceVideoScraper: attachIfPossible connect/tap failed"
      );
      throw err;
    }
  }

  private static installHook(): boolean {
    if (VoiceVideoScraper.waRecInstalled) return false;

    const OldAWN = window.AudioWorkletNode;
    VoiceVideoScraper.originalAudioWorkletNode = OldAWN;

    if (!OldAWN) {
      VoiceVideoScraper.staticLogger?.warn({}, "VoiceVideoScraper: installHook skipped (AudioWorkletNode not available)");
      return false;
    }

    VoiceVideoScraper.waRecInstalled = true;

    const NewAWN = function (this: AudioWorkletNode, ctx: BaseAudioContext, name: string | { name: string }, opts?: AudioWorkletNodeOptions) {
      const nodeName = typeof name === "string" ? name : (name as { name: string })?.name;
      const node = new (OldAWN as any)(ctx, name as any, opts);

      try {
        if (String(nodeName) === (VoiceVideoScraper.selectors?.callCollection?.workletNode ?? TARGET_NODE_NAME)) {
          VoiceVideoScraper.state.targetNode = node;

          if (VoiceVideoScraper.state.started && !VoiceVideoScraper.state.attached) {
            void VoiceVideoScraper.attachIfPossible().catch((err) => {
              VoiceVideoScraper.staticLogger?.error(
                { err: err instanceof Error ? err.message : String(err) },
                "VoiceVideoScraper: attachIfPossible (from hook) failed"
              );
            });
          }
        }
      } catch (err) {
        VoiceVideoScraper.staticLogger?.warn(
          { err: err instanceof Error ? err.message : String(err), nodeName: String(nodeName) },
          "VoiceVideoScraper: hook handler error (non-fatal)"
        );
      }

      return node;
    };

    (NewAWN as any).prototype = OldAWN.prototype;
    Object.setPrototypeOf(NewAWN, OldAWN);
    window.AudioWorkletNode = NewAWN as unknown as typeof AudioWorkletNode;
    return true;
  }

  static restoreOriginalWindow(): void {
    VoiceVideoScraper.cleanupRecordingResources();
    VoiceVideoScraper.activeCall = null;
    if (typeof window === "undefined") return;
    if (!VoiceVideoScraper.waRecInstalled) return;

    if (VoiceVideoScraper.originalAudioWorkletNode) {
      (window as any).AudioWorkletNode = VoiceVideoScraper.originalAudioWorkletNode;
      VoiceVideoScraper.originalAudioWorkletNode = null;
    }
    VoiceVideoScraper.waRecInstalled = false;
  }

  async start() {
    const { state } = VoiceVideoScraper;

    state.started = true;
    state.attached = false;
    state.frames = [];
    state.tapNode = null;

    VoiceVideoScraper.disconnectPreviousMic();

    try {
      await VoiceVideoScraper.attachIfPossible();
    } catch (err) {
      this.logger.error({ err: err instanceof Error ? err.message : String(err) }, "VoiceVideoScraper.start: attachIfPossible failed");
      throw err;
    }

    return { ok: true, waiting: !state.attached };
  }

  stop(): StopReturnType {
    const { state } = VoiceVideoScraper;

    if (!state.started) {
      this.logger.warn({}, "VoiceVideoScraper.stop: called but not started");
      throw new Error("Not started. Call start first.");
    }
    state.started = false;

    VoiceVideoScraper.disconnectPreviousMic();
    VoiceVideoScraper.stopMicStream();

    if (!state.attached || !state.tapNode || !state.targetNode) {
      this.logger.warn(
        { attached: state.attached, hasTapNode: Boolean(state.tapNode), hasTargetNode: Boolean(state.targetNode) },
        "VoiceVideoScraper.stop: recorder not attached (no target node seen)"
      );
      throw new Error("Recorder not attached yet (no target node seen). Join or continue the call first.");
    }

    const sr = (state.targetNode.context as BaseAudioContext).sampleRate || 16000;
    const pcm = VoiceVideoScraper.flattenFrames(state.frames);

    if (!pcm.length) {
      this.logger.warn({ frameCount: state.frames.length }, "VoiceVideoScraper.stop: no audio captured");
      throw new Error("No audio captured yet.");
    }

    if (state.gain !== 1) {
      for (let i = 0; i < pcm.length; i++) {
        let v = pcm[i] * state.gain;
        if (v > 1) v = 1;
        if (v < -1) v = -1;
        pcm[i] = v;
      }
    }

    const wavArrayBuffer = VoiceVideoScraper.encodeWav16(pcm, sr);
    const durationSec = pcm.length / sr;
    const filename = typeof state.filename === "function" ? state.filename() : String(state.filename);

    try {
      state.targetNode?.disconnect(state.tapNode!);
    } catch {
      // ignore if already disconnected
    }
    try {
      state.tapNode?.disconnect();
    } catch {
      // ignore if already disconnected
    }

    state.attached = false;
    state.tapNode = null;
    state.targetNode = null;

    return { arrayBuffer: wavArrayBuffer, filename, sampleRate: sr, durationSec };
  }

  private getModelId(idFnBind: any | undefined) {
    if (!this.selectors || !idFnBind) return;

    const id: string | undefined = idFnBind?.toString();

    if (id?.endsWith(WAP_LID_SUFFIX)) {
      try {
        const WAWebApiContact = getNamespace(this.selectors.ng?.WAWebApiContact?.find ?? ["WAWebApiContact"]);
        const getPhoneNumber = get(WAWebApiContact, this.selectors.ng?.WAWebApiContact?.getPhoneNumber ?? "getPhoneNumber");
        const lid = getPhoneNumber?.bind(WAWebApiContact)(idFnBind);
        const lidFn = get(lid, this.selectors.ng?.WAWebApiContact?.lidFnBind ?? "toString");
        return lidFn?.bind(lid)() as string | undefined;
      } catch {
        return id;
      }
    }

    return id;
  }

  private extractPhoneFromId(phoneId: string | undefined) {
    const phoneValue = phoneId?.split("@")[0] ?? "";
    const nonNumericCharacter = phoneValue.search(/[^0-9]/);
    if (nonNumericCharacter !== -1) {
      return phoneValue.substring(0, nonNumericCharacter);
    }
    return phoneValue;
  }

  private static getModelIdFromModel(model: ModelType | undefined) {
    const selectors = VoiceVideoScraper.selectors;
    if (!selectors || !model) return;

    const idFnBind = get(model, selectors.modelMap.idFnBind);
    const idFn = get(model, selectors.modelMap.idFn);
    const id: string | undefined = idFn?.bind(idFnBind)();

    if (id?.endsWith(WAP_LID_SUFFIX)) {
      try {
        const WAWebApiContact = getNamespace(selectors.ng?.WAWebApiContact?.find ?? ["WAWebApiContact"]);
        const getPhoneNumber = get(WAWebApiContact, selectors.ng?.WAWebApiContact?.getPhoneNumber ?? "getPhoneNumber");
        const lid = getPhoneNumber?.bind(WAWebApiContact)(idFnBind);
        const lidFn = get(lid, selectors.ng?.WAWebApiContact?.lidFnBind ?? "toString");
        return lidFn?.bind(lid)() as string | undefined;
      } catch {
        return id;
      }
    }

    return id;
  }

  static getContactPhone(contact?: ContactModel): string {
    if (!VoiceVideoScraper.selectors) {
      throw new Error("MessageScraper.inject must be called before usage");
    }
    const contactId = VoiceVideoScraper.getModelIdFromModel(contact as any);
    return contactId?.split("@")[0] ?? get(contact, VoiceVideoScraper.selectors.contact.phone);
  }

  static getDisplayName(model: ContactModel | ModelType) {
    if (!VoiceVideoScraper.selectors) {
      throw new Error("MessageScraper.inject must be called before usage");
    }
    const modelContact = (window as any).importNamespace("WAWebContactCollection").ContactCollection.get(model);
    if (modelContact) {
      const val = (window as any).importNamespace("WAWebContactGetters").getName(modelContact);
      if (val) return val;
    }

    const displayNameAttr = VoiceVideoScraper.selectors.contact.displayNameArray.find((item) => get(model, item));
    if (!displayNameAttr) return getPrettyPhone(VoiceVideoScraper.getContactPhone(model));
    return get(model, displayNameAttr);
  }

  #me: { id?: string; server: string; phone?: string; isBusiness?: boolean; name?: string } | undefined;
  get me() {
    this.#me ??= this.instantMessagingService.getMe();
    return this.#me;
  }

  async *[Symbol.asyncIterator](): AsyncGenerator<{ data: StopReturnType; activeCall: ActiveCall }> {
    try {
      for await (const { isConnected } of this.newActiveCallIterator) {
        this.settings.abortSignal.throwIfAborted();

        if (!isConnected) {
          try {
            const data = this.stop();
            if (!VoiceVideoScraper.activeCall) {
              continue;
            }
            yield { data, activeCall: VoiceVideoScraper.activeCall };
          } catch (err) {
            this.logger.error({ err: err instanceof Error ? err.message : String(err) }, "VoiceVideoScraper: stop() failed on disconnect");
            continue;
          }
          continue;
        }

        const activeCall = get(this.whatsappAPI, this.selectors.callCollection?.activeCall ?? "CallCollection.activeCall");
        if (!activeCall) continue;

        const fromIdModel = get(activeCall, this.selectors?.activeCall?.fromIdModel ?? "msg.__x_from");
        const toIdModel = get(activeCall, this.selectors?.activeCall?.toIdModel ?? "msg.__x_to");
        const isSentByMe = get(activeCall, this.selectors?.activeCall?.isSentByMe);
        const contactModal = isSentByMe ? toIdModel : fromIdModel;
        const contactId = this.getModelId(contactModal);
        const contactName = VoiceVideoScraper.getDisplayName(contactModal);
        const sender: { id: string | undefined; name: string | undefined; phone: string | undefined } = isSentByMe
          ? { id: this.me?.id, name: this.me?.name, phone: this.me?.phone }
          : { id: contactId, name: contactName, phone: this.extractPhoneFromId(contactId) };
        const recipient: { id: string | undefined; name: string | undefined; phone: string | undefined } = isSentByMe
          ? { id: contactId, name: contactName, phone: this.extractPhoneFromId(contactId) }
          : { id: this.me?.id, name: this.me?.name, phone: this.me?.phone };

        if (!contactId) continue;

        const allModels = this.instantMessagingService.getAllModels?.() ?? [];
        const fromModel = allModels.find((model) => (model as any).id === fromIdModel);
        const toModel = allModels.find((model) => (model as any).id === toIdModel);

        if (!fromModel || !toModel) continue;

        VoiceVideoScraper.activeCall = {
          isSentByMe,

          senderName: sender.name ?? "",
          senderId: sender.id ?? "",
          senderPhone: sender.phone ?? "",

          receiverName: recipient.name ?? "",
          receiverId: recipient.id ?? "",
          receiverPhone: recipient.phone ?? "",

          isGroupMsg: get(activeCall, this.selectors?.activeCall?.isGroup ?? "msg.isGroup"),

          t: get(activeCall, this.selectors?.activeCall?.t ?? "msg.__x_t"),
          startOfDay: get(activeCall, this.selectors?.activeCall?.startOfDay ?? "msg.__x_startOfDay"),

          id: `c${get(activeCall, this.selectors?.activeCall?.msgId ?? "__x_id")}c`,
        };

        try {
          await this.start();
        } catch (err) {
          this.logger.error(
            { err: err instanceof Error ? err.message : String(err), callId: VoiceVideoScraper.activeCall.id },
            "VoiceVideoScraper: start() failed on call connect"
          );
          throw err;
        }
      }
    } catch (err) {
      if (this.settings.abortSignal.aborted) {
        VoiceVideoScraper.cleanupRecordingResources();
        VoiceVideoScraper.activeCall = null;
        return;
      }
      this.logger.error(
        { err: err instanceof Error ? err.message : String(err), name: err instanceof Error ? err.name : undefined },
        "VoiceVideoScraper: async iterator error"
      );
      throw err;
    }
  }
}
